import type { InsightsDashboard, GlobalSelection, InsightsSource } from "@/infrastructure/types";
import { useDelete, useFetch, useUpdate } from "@/plugins/api";
import type { Layout } from "grid-layout-plus";
import { defineStore } from "pinia";
import { useRouter, type RouteLocationRaw } from "vue-router";

interface DashboardUpdateModel {
  name: string
  description: string
  isShared: boolean
  period: string
  filters: Record<string, string[]>
  chartIds: number[]
  layout: Layout
}

export const useInsightsStore = defineStore('insights', () => {
  const router = useRouter()
  const dashboards = ref<InsightsDashboard[]>([])
  const globalSelection = ref<GlobalSelection>()
  const lastLoadedDashboardId = ref<number | undefined>('insights-dashboard-id' in localStorage ? parseInt(localStorage.getItem('insights-dashboard-id') as string) : undefined)
  const currentDashboard = ref<InsightsDashboard>()
  const backRoute = ref<RouteLocationRaw>({ name: 'insights' })

  function loadGlobalSelectionFromLocalStorage(): GlobalSelection | undefined {
    const previousSelection = 'insights:global-selection' in localStorage ? JSON.parse(localStorage.getItem('insights:global-selection') as string) as GlobalSelection : undefined

    globalSelection.value = previousSelection
    return previousSelection
  }

  async function getDashboards() {
    try {
      const data = await useFetch('insights/dashboards', { includeShared: true }) as InsightsDashboard[]

      // Convert periods that are absolute
      dashboards.value = data.map(d => parseDashboard(d))
    } catch (error) {
      console.log(error)
    }
  }
  async function getDashboardById(id: number) {
    const data = await useFetch(`insights/dashboards/${id}`) as InsightsDashboard

    // Convert period if absolute
    return parseDashboard(data)
  }

  async function saveDashboard(dashboard: InsightsDashboard) {
    const { name, description, isShared, period, filters, charts, layout } = dashboard

    const updateModel: Partial<DashboardUpdateModel> = {
      name,
      description,
      isShared,
      period: Array.isArray(period) ? JSON.stringify(period) : period,
      filters,
      chartIds: charts.map((chart) => chart.id),
      layout
    }

    await useUpdate(`insights/dashboards/${dashboard.id}`, updateModel)
  }

  async function getSources(): Promise<InsightsSource[]> {
    const data = await useFetch('insights/sources')
    return data
  }

  async function deleteDashboard(id: number) {
    await useDelete(`insights/dashboards/${id}`)
  }

  async function deleteChart(id: number) {
    await useDelete(`insights/charts/${id}`)
  }

  function parseDashboard(dashboard: InsightsDashboard): InsightsDashboard {
    return {
      ...dashboard,
      period: dashboard.period?.includes('[') ? JSON.parse(dashboard.period) : dashboard.period
    }
  }

  watch(globalSelection, (selection) => {
    // Save the selection to localStorage
    localStorage.setItem('insights:global-selection', JSON.stringify(selection))
  }, {
    deep: true
  })

  watch(currentDashboard, (value) => {
    if (value) {
      localStorage.setItem('insights-dashboard-id', value.id.toString())
      lastLoadedDashboardId.value = value.id
    }
  })

  watch(router.currentRoute, (route) => {
    if (route.name === 'insights') {
      backRoute.value = route
    }
  })

  return {
    dashboards,
    globalSelection,
    lastLoadedDashboardId,
    currentDashboard,
    backRoute,
    getDashboards,
    getDashboardById,
    saveDashboard,
    deleteDashboard,
    deleteChart,
    getSources,
    loadGlobalSelectionFromLocalStorage
  }
})